import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ErrorService } from '@app/services/error.service';
import { FilterParams } from '@claranet/node-search-filter';
import { BusinessCode } from '@data//models/accounting-sage.model';
import {
    BusinessProject,
    EmbedBusinessProject,
} from '@data/models/business-project.model';
import { Embedded } from '@data/models/embed.model';
import { Opportunity } from '@data/models/opportunity.model';
import { toParams } from '@shared/utils/query-to-params';
import {
    Observable,
    of,
} from 'rxjs';
import {
    catchError,
    map,
} from 'rxjs/operators';

interface UpdateOpportunityPayload {
    groups: {
        groupId: string;
        factoryGroup: string;
        quoteLineItems: {
            id: string;
            factory: string;
        }[];
    }[];
}

export const BUSINESS_PROJECTS_SERVICE_API_ROOT = '/core/v1/business-projects';
@Injectable()
export class BusinessProjectService {

    private errors;

    constructor(
        private errorService: ErrorService,
        private http: HttpClient
    ) {
        this.errors = this.errorService.businessProjectsServiceErrors;
    }

    /**
     * Get business-code
     *
     * @returns {Observable<BusinessCode>} - The business-code
     *
     * @memberof BusinessProjectService
     */
    public getBusinessCode(businessCode: string): Observable<BusinessCode> {
        return this.http.get<BusinessProject>(`${BUSINESS_PROJECTS_SERVICE_API_ROOT}/${businessCode}`).pipe(
            catchError(this.errorService.errorHandler<BusinessProject>(this.errors, 'Récupération du code affaire salesforce', null)),
            map(this.businessProjectToBusinessCode)
        );
    }

    /**
     * Get sorted business-code list
     *
     * @returns {Observable<BusinessCode[]>} - The business-code list
     *
     * @memberof BusinessProjectService
     */
    public findBusinessCodes(query?: FilterParams): Observable<BusinessCode[]> {
        const params = toParams(query);
        return this.http.get<{ businessProjects: BusinessProject[] }>(BUSINESS_PROJECTS_SERVICE_API_ROOT, { params }).pipe(
            map((result) => result.businessProjects),
            catchError(this.errorService.errorHandler<BusinessProject[]>(this.errors, 'Récupération des business-projects', [])),
            map((businessProjects) => {
                return businessProjects.map(this.businessProjectToBusinessCode);
            })
        );
    }

    public findBusinessProjectsOpportunities(businessCode: string): Observable<Opportunity[]> {
        return this.http.get<{ opportunities: Opportunity[] }>(
            `${BUSINESS_PROJECTS_SERVICE_API_ROOT}/${businessCode}/opportunities`,
        ).pipe(
            map(
                ({ opportunities }) => opportunities
                    .map(opportunity => new Opportunity(opportunity))
                    .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
            ),
            catchError(this.errorService.errorHandler<Opportunity[]>(this.errors, 'Récupération des opportunités', []))
        );
    }

    public findBusinessProjectsOpportunitiesWithEmbedded(businessCode: string, query?: FilterParams & { embed: string }):
    Observable<{ opportunities: Opportunity[]; embedded: Embedded }> {
        const params = toParams(query);
        return this.http.get<{ opportunities: Opportunity[]; embedded: Embedded }>(
            `${BUSINESS_PROJECTS_SERVICE_API_ROOT}/${businessCode}/opportunities`, { params }
        ).pipe(
            map(
                ({ opportunities, embedded }) => ({
                    opportunities: opportunities
                        .map(opportunity => new Opportunity(opportunity))
                        .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()),
                    embedded
                })
            ),
            catchError(this.errorService.errorHandler<{ opportunities: Opportunity[]; embedded: Embedded }>
            (this.errors, 'Récupération des opportunités', { opportunities: [], embedded: {} }))
        );
    }

    public getBusinessProject(businessCode: string): Observable<BusinessProject | null> {
        if (!businessCode || !(businessCode.startsWith('BPJ') && businessCode.length === 9)) {
            return of(null);
        }
        return this.http.get<BusinessProject>(`${BUSINESS_PROJECTS_SERVICE_API_ROOT}/${businessCode}`).pipe(
            map((businessProject) => new BusinessProject(businessProject)),
            catchError(this.errorService.errorHandler<BusinessProject>(this.errors, 'Récupération du business-project', null))
        );
    }

    public getBusinessProjectEmbedded(businessCode: string): Observable<EmbedBusinessProject> {
        if (!businessCode) {
            return of(null);
        }
        return this.http.get<BusinessProject & { embedded: Embedded }>(`${BUSINESS_PROJECTS_SERVICE_API_ROOT}/${businessCode}?embed=companies,users`).pipe(
            map((businessProject) => BusinessProject.toBusinessProjectEmbedded(new BusinessProject(businessProject), businessProject.embedded)),
            catchError(this.errorService.errorHandler<EmbedBusinessProject>(this.errors, 'Récupération du business-project', null))
        );
    }

    public updateBusinessProject(businessProject: BusinessProject): Observable<BusinessProject> {
        return this.http.patch<BusinessProject>(`${BUSINESS_PROJECTS_SERVICE_API_ROOT}/${businessProject.id}`, businessProject)
            .pipe(
                map(_project => new BusinessProject(_project)),
                catchError(this.errorService.errorHandler<BusinessProject>(this.errors, 'Modification d\'un business projet', null))
            );
    }

    public findEmbeddedBusinessProjects(query?: FilterParams): Observable<EmbedBusinessProject[]> {
        const params = toParams(query);
        return this.http.get<{ businessProjects: BusinessProject[]; embedded: Embedded }>(BUSINESS_PROJECTS_SERVICE_API_ROOT, { params }).pipe(
            map(({ businessProjects, embedded }) =>
                businessProjects.map((businessProject) => BusinessProject.toBusinessProjectEmbedded(new BusinessProject(businessProject), embedded))
            ),
            catchError(this.errorService.errorHandler<EmbedBusinessProject[]>(this.errors, 'Récupération des business-projects', []))
        );
    }

    public findBusinessProjects(query?: FilterParams): Observable<BusinessProject[]> {
        const params = toParams(query);
        return this.http.get<{ businessProjects: BusinessProject[] }>(BUSINESS_PROJECTS_SERVICE_API_ROOT, { params }).pipe(
            map(({ businessProjects }) =>
                businessProjects.map((businessProject) => new BusinessProject(businessProject))
            ),
            catchError(this.errorService.errorHandler<BusinessProject[]>(this.errors, 'Récupération des business-projects', []))
        );
    }

    public cancelOpportunity(opportunity: Opportunity) {
        return this.http.patch<Opportunity>(`${BUSINESS_PROJECTS_SERVICE_API_ROOT}/
        ${opportunity.businessProject}/opportunities/${opportunity.quoteId}`, { cancellationDate: opportunity.cancellationDate })
            .pipe(
                map(_op => new Opportunity(_op)),
                catchError(this.errorService.errorHandler<Opportunity>(this.errors, 'Annulation d\'une opportunité', null))
            );
    }

    public updateOpportunity(businessProjectId: string, quoteId: string, payload: UpdateOpportunityPayload) {
        return this.http.patch(`${BUSINESS_PROJECTS_SERVICE_API_ROOT}/${businessProjectId}/opportunities/${quoteId}`, payload);
    }

    private businessProjectToBusinessCode(businessProject: BusinessProject | null): BusinessCode {
        if (!businessProject) {
            return;
        }
        return {
            source: 'synapps',
            businessCode: businessProject.id,
            createdAt: businessProject.createdAt,
            businessLabel: businessProject.name,
            defaultClient: businessProject.operationalClient,
        };
    }

}
